import type {
  AreaNextStep,
  AttentionItem,
  DiscoverySummary,
  DuplicateRow,
  MatchDecision,
  PlanRole,
  StartDiscoveryBody,
} from '@/lib/api/discovery';
import type { ClientFact, FactSourceKind, PutClientFactBody } from '@/lib/api/client-facts';

/**
 * Pure helpers behind Engagement -> Discovery (T-0185). They only word
 * and arrange what the API computed: every count, status and label shown
 * comes from the server (`@caliber-2/discovery-core`); nothing here
 * counts evidence, sources or interviews.
 *
 * Design reference: `docs/discovery/prototype/discovery.js` (`engPage`,
 * `guideView`, `attentionRow`, `coverageView`, `matrixView`, `setupView`).
 */

export const plural = (n: number, word: string, many = `${word}s`) =>
  `${n.toLocaleString('en-US')} ${n === 1 ? word : many}`;

// ---------------------------------------------------------------------------
// Sections and tabs
// ---------------------------------------------------------------------------

export type DiscoverySection = 'overview' | 'sessions' | 'findings' | 'maps' | 'setup' | 'signoff';

/**
 * Which Discovery screen a path is on (`/engagements/:id/discovery[/<section>]`),
 * or null when the path has left the module. The layout route reads the
 * router's location, which already names the destination while the person
 * is navigating away (a sidebar link, Cancel on Set up); treating that path
 * as Discovery's own Overview made the one-direction rule send them back to
 * Set up. Outside the module there is nothing to route.
 */
export function sectionOf(pathname: string): DiscoverySection | null {
  const m = /\/discovery(?:\/([^/?#]+))?(?:\/|[?#]|$)/.exec(pathname);
  if (!m) return null;
  const s = m[1];
  return s === 'sessions' || s === 'findings' || s === 'maps' || s === 'setup' || s === 'signoff'
    ? s
    : 'overview';
}

/** Four tabs (Decision 49): people live in Sessions, pain points in Findings, sign-off is an Overview card. */
export const DISCOVERY_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'sessions', label: 'Sessions' },
  { id: 'findings', label: 'Findings' },
  { id: 'maps', label: 'Process maps' },
] as const;
export type DiscoveryTab = (typeof DISCOVERY_TABS)[number]['id'];

const stepCounts = (s: DiscoverySummary, key: string) =>
  s.guide.steps.find((x) => x.key === key)?.counts ?? {};

/** Tab counts, all from the summary: interviews, confirmed evidence, processes to map. */
export function tabCounts(s: DiscoverySummary): Record<Exclude<DiscoveryTab, 'overview'>, number> {
  return {
    sessions: s.kpis.interviews_total,
    findings: stepCounts(s, 'evidence')['confirmed'] ?? 0,
    maps: stepCounts(s, 'close_gaps')['processes_total'] ?? 0,
  };
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

export function sourcesRuleText(
  s: Pick<DiscoverySummary, 'corroboration_off' | 'sources_required'>,
) {
  return s.corroboration_off
    ? 'corroboration is turned off: one source covers an area'
    : `each needs ${s.sources_required ?? 2} independent sources`;
}

export function leadText(lead: DiscoverySummary['lead']): string | null {
  if (lead.length === 0) return null;
  const names = lead.map((l) => l.name);
  if (names.length === 1) return `lead ${names[0]}`;
  return `leads ${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;
}

/** The newer published version the engagement could move to, if any. */
export function newerVersion(s: DiscoverySummary): number | null {
  if (!s.framework || s.latest_version === null) return null;
  return s.latest_version > s.framework.version ? s.latest_version : null;
}

// ---------------------------------------------------------------------------
// Guide (Decision 47, 54.9): the API ticks each step; this words it.
// ---------------------------------------------------------------------------

/**
 * Where a step leads (E23-design §9): set-up opens Client facts (the set-up
 * screen is one-direction and closes once discovery starts), evidence opens
 * Sessions, close the gaps opens Findings, requirements and sign-off opens
 * Sign-off.
 */
export type GuideAction =
  | {
      kind: 'client_facts' | 'sessions' | 'findings' | 'signoff' | 'review' | 'intake';
      label: string;
    }
  /** One interview: the one whose speakers to match. */
  | { kind: 'interview'; id: string; label: string };

/**
 * A pinned version that defines no processes (C2-74). HRIS & payroll v1
 * came from Caliber v1 with none. Processes are added per area in Firm
 * settings > Discovery frameworks and published as a new version; the
 * engagement lead then moves the engagement to it (rule 4). The guide words
 * it on Close the gaps (maps are a count there, never a condition); the
 * dashboard's process maps card gives firm admins and curators the link.
 */
export const NO_PROCESSES_TITLE = 'No processes to map';
export const NO_PROCESSES_TEXT = 'This framework defines no processes to map.';
export const FRAMEWORKS_LINK_LABEL = 'Open Discovery frameworks';

export function noProcessesHelp(canCurateFrameworks: boolean): string {
  return canCurateFrameworks
    ? 'Add them to each area in Firm settings > Discovery frameworks and publish a new version, then move this engagement to it.'
    : 'A firm admin adds them in Firm settings > Discovery frameworks and publishes a new version; the engagement lead then moves this engagement to it.';
}

export interface WordingOptions {
  /** From `GET /api/v1/me`: the reader may change frameworks in Firm settings. */
  canCurateFrameworks?: boolean;
  /**
   * The interviews whose items wait on their speakers, named as the list
   * names them (the interview list's `match_speakers` state), first first.
   */
  matchSpeakers?: ReadonlyArray<{ id: string; name: string }>;
}

export interface GuideStepView {
  key: string;
  title: string;
  state: 'done' | 'current' | 'todo' | 'locked';
  text: string;
  hint: string | null;
  ai: string | null;
  /** The step's links, the first the main one; empty until the step is current or done. */
  actions: GuideAction[];
}

const GUIDE_AI: Record<string, string> = {
  evidence:
    'AI reads each transcript and client document, matches speakers to people and proposes evidence tagged to areas. Nothing counts until you confirm it.',
  close_gaps:
    'Code counts the sources. AI drafts follow-up questions for thin areas and a current-state map for each process with evidence; you edit and the client validates.',
  requirements_signoff:
    'AI matches what people said to the requirement library and drafts the rest. You add them in Selection; the client signs from a secure link.',
};

/**
 * The four steps in words (T-0262). Every number is a count the API put on
 * the step; the words only arrange them. A step offers its link once it is
 * current or done.
 */
export function guideSteps(s: DiscoverySummary, opts: WordingOptions = {}): GuideStepView[] {
  const areas = s.kpis.areas;
  return s.guide.steps.map((step) => {
    const c = step.counts;
    const n = (key: string) => c[key] ?? 0;
    let text = '';
    let actions: GuideAction[] = [];
    switch (step.key) {
      case 'setup':
        text = s.framework
          ? `${s.framework.name} framework v${s.framework.version} · ${plural(areas, 'area')} · ${n('client_facts_set')} of ${n('client_facts_total')} client facts · ${n('named')} of ${plural(n('planned'), 'planned interview')} have a person.`
          : 'Choose the framework, check the client facts and name who you’ll interview.';
        actions = [{ kind: 'client_facts', label: 'Client facts' }];
        break;
      case 'evidence': {
        // An API from before the room step sends neither count: everything waiting is ready.
        const ready = 'ready' in c ? n('ready') : n('waiting');
        const speakers = n('speakers_to_match');
        const waiting = [
          n('to_place') > 0 ? `${plural(n('to_place'), 'transcript')} to place` : null,
          n('reading') > 0 ? `${plural(n('reading'), 'transcript')} being read` : null,
          speakers > 0
            ? `${plural(speakers, 'item waits', 'items wait')} on speaker matching`
            : null,
          ready > 0 ? `${plural(ready, 'item')} waiting for review` : null,
        ].filter((x): x is string => x !== null);
        const nothingYet =
          n('held') + n('documents_read') + n('confirmed') === 0 && waiting.length === 0;
        text = nothingYet
          ? 'Hold the interviews and upload each transcript right after; upload the documents the client already has. AI proposes evidence; you confirm it.'
          : `${n('held')} of ${plural(n('total'), 'interview')} held · ${plural(n('documents_read'), 'document')} read · ${plural(n('confirmed'), 'evidence item')} confirmed.${waiting.length ? ` ${waiting.join(' · ')}.` : ''}`;
        // Step 2 links straight to the three things it counts (design note section 3).
        const match = opts.matchSpeakers ?? [];
        actions = [
          ...(ready > 0
            ? [{ kind: 'review' as const, label: `Review ${plural(ready, 'item')}` }]
            : []),
          { kind: 'intake' as const, label: 'Bring in files' },
          ...(speakers > 0 && match.length > 0
            ? [
                {
                  kind: 'interview' as const,
                  id: match[0]!.id,
                  label:
                    match.length === 1
                      ? `Match the speakers of ${match[0]!.name}`
                      : `Match speakers in ${plural(match.length, 'interview')}`,
                },
              ]
            : []),
        ];
        break;
      }
      case 'close_gaps': {
        const rule = s.corroboration_off
          ? `Each of the ${areas} areas needs one source. Corroboration is turned off for this framework.`
          : `Each of the ${areas} areas needs ${s.sources_required ?? 2} independent sources: people or documents.`;
        const progress = [
          `${n('covered')} of ${plural(n('total'), 'area')} covered`,
          ...(n('questions') > 0
            ? [`${plural(n('questions'), 'follow-up question')} drafted`]
            : []),
        ].join(' · ');
        // A version with no processes (C2-74) is worded, never a block; the
        // dashboard's process maps card shows a curator the way to add them.
        const maps =
          s.started && n('processes_total') === 0
            ? `${NO_PROCESSES_TEXT} ${noProcessesHelp(opts.canCurateFrameworks === true)}`
            : `${n('processes_ready')} of ${plural(n('processes_total'), 'process', 'processes')} ready to map, ${n('processes_validated')} validated.`;
        text = `${rule} ${progress}. ${maps}`;
        actions = [{ kind: 'findings', label: 'Open Findings' }];
        break;
      }
      case 'requirements_signoff': {
        const suggestions =
          n('suggestion_runs') === 0
            ? 'No requirement suggestions drafted yet'
            : n('open_suggestions') > 0
              ? `${plural(n('open_suggestions'), 'requirement suggestion')} open`
              : 'Every requirement suggestion decided';
        text = `${suggestions} · ${plural(n('requirements_total'), 'requirement')} · sign-off checklist ${n('checklist_done')} of ${n('checklist_total')}. Suggestions go to Selection → Requirements; the client signs from a secure link, and export stays locked until then.`;
        actions = [{ kind: 'signoff', label: 'Open sign-off' }];
        break;
      }
    }
    return {
      key: step.key,
      title: step.title,
      state: step.state,
      text,
      hint: step.state === 'locked' ? step.unlock_hint : null,
      ai: GUIDE_AI[step.key] ?? null,
      actions: step.state === 'current' || step.state === 'done' ? actions : [],
    };
  });
}

// ---------------------------------------------------------------------------
// Attention cards (Decision 54.40): one per API item, in the API's order.
// ---------------------------------------------------------------------------

export type DiscoveryDestination =
  | { to: 'overview' }
  | {
      to: 'sessions';
      interview?: string;
      person?: string;
      /** The review queue: `all`, an interview id, `doc:<id>` or `area:<id>`. */
      review?: string;
      intake?: boolean;
    }
  | { to: 'findings'; area?: string; status?: string }
  | { to: 'maps' }
  | { to: 'signoff' }
  | { to: 'selection'; add?: 'discovery' }
  /** Firm settings > Discovery frameworks, where processes are added (C2-74). */
  | { to: 'frameworks' };

export interface AttentionCardView {
  kind: string;
  tone: 'ai' | 'warn' | 'ok' | 'neutral';
  title: string;
  text: string;
  action: { label: string; destination: DiscoveryDestination } | null;
}

const SIGNOFF_WORDS: Record<string, [string, string]> = {
  signed: ['Requirements signed off', 'The client signed from a secure link. Export is unlocked.'],
  waived: ['Sign-off waived', 'The waiver and its reason are printed on every exported packet.'],
  requested: ['Sign-off requested', 'Waiting for the client’s signature from their secure link.'],
  changes_requested: [
    'The client asked for changes',
    'Review what they asked, then request sign-off again.',
  ],
  reopened: ['Sign-off reopened', 'The requirements changed after the snapshot. Request it again.'],
};

export function attentionCard(item: AttentionItem, opts: WordingOptions = {}): AttentionCardView {
  const c = item.counts;
  const tone: AttentionCardView['tone'] =
    item.tone === 'warn'
      ? 'warn'
      : item.tone === 'ok'
        ? 'ok'
        : item.kind === 'review_evidence'
          ? 'ai'
          : 'neutral';
  switch (item.kind) {
    case 'review_evidence': {
      const proposed = c['proposed'] ?? 0;
      if (proposed === 0 || !item.target) {
        return {
          kind: item.kind,
          tone,
          title: 'Review queue is clear',
          text: 'Every AI proposal has been decided.',
          action: null,
        };
      }
      const rows = item.breakdown ?? [];
      const where = rows.map((r) => `${r.code ?? 'Other'} (${r.count})`).join(', ');
      const first = rows.find((r) => r.interview_id === item.target?.interview_id);
      return {
        kind: item.kind,
        tone,
        title: `${plural(proposed, 'evidence item')} to review`,
        text: `${where ? `${where}. ` : ''}Nothing counts until you confirm.`,
        // One review queue across interviews (23 Oct 2026); the first interview's
        // code names the button when there is one.
        action: {
          label: first?.code ? `Review ${first.code}` : 'Review',
          destination:
            item.target.kind === 'interview'
              ? { to: 'sessions', review: item.target.interview_id ?? 'all' }
              : { to: 'sessions', review: 'all' },
        },
      };
    }
    case 'possible_duplicates': {
      const n = c['people'] ?? 1;
      return {
        kind: item.kind,
        tone,
        title: n === 1 ? 'Possible duplicate person' : `${n} possible duplicate people`,
        text: 'Someone may have been entered twice. Merge them so they count once.',
        action: {
          label: 'Review',
          destination: { to: 'sessions', person: item.target?.person_id },
        },
      };
    }
    case 'requirement_suggestions': {
      const open = c['open'] ?? 0;
      return {
        kind: item.kind,
        tone,
        title:
          open > 0 ? `${plural(open, 'requirement suggestion')} ready` : 'No suggestions waiting',
        text: 'Built from confirmed evidence. They are added in Selection.',
        action: {
          label: open > 0 ? 'Review' : 'Open',
          destination: { to: 'selection', add: 'discovery' },
        },
      };
    }
    case 'process_maps': {
      const ready = c['ready'] ?? 0;
      if ((c['total'] ?? 0) === 0) {
        // The pinned version defines no processes: nothing is "up to date".
        return {
          kind: item.kind,
          tone: 'neutral',
          title: NO_PROCESSES_TITLE,
          text: `${NO_PROCESSES_TEXT} ${noProcessesHelp(opts.canCurateFrameworks === true)}`,
          action: opts.canCurateFrameworks
            ? { label: FRAMEWORKS_LINK_LABEL, destination: { to: 'frameworks' } }
            : null,
        };
      }
      return {
        kind: item.kind,
        tone,
        title:
          ready > 0
            ? `${plural(ready, 'process', 'processes')} ready to map`
            : 'Process maps up to date',
        text:
          ready > 0
            ? 'Enough confirmed evidence to draft a current-state map.'
            : 'Every process with evidence has a map.',
        action: { label: 'Open', destination: { to: 'maps' } },
      };
    }
    case 'signoff': {
      const named = item.state ? SIGNOFF_WORDS[item.state] : undefined;
      const open = c['open_conditions'] ?? 0;
      return {
        kind: item.kind,
        tone,
        title:
          named?.[0] ??
          `Sign-off: ${open > 0 ? `${open} of ${c['conditions'] ?? open} conditions open` : 'ready to request'}`,
        text: named?.[1] ?? 'The client signs from a secure link. Export stays locked until then.',
        action: { label: 'Open', destination: { to: 'signoff' } },
      };
    }
    default:
      return { kind: item.kind, tone, title: item.kind.replace(/_/g, ' '), text: '', action: null };
  }
}

// ---------------------------------------------------------------------------
// Coverage
// ---------------------------------------------------------------------------

/** Meter cells (`.meter`): one per required source, more when an area has extra. */
export function meterCells(sources: number, required: number, status: string) {
  return Array.from({ length: Math.max(required, sources) }, (_, i) =>
    i < sources ? (status === 'corroborated' ? 'on' : 'one') : 'off',
  );
}

export function formatDay(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** The area's next step in words (E12-F06 §2); the step itself is computed by the API. */
export function nextStepText(
  step: AreaNextStep,
  names: ReadonlyMap<string, string>,
): { text: string; done: boolean } {
  switch (step.kind) {
    case 'done':
      return { text: `Covered by ${plural(step.sources ?? 0, 'independent source')}`, done: true };
    case 'review': {
      const who = step.source_key ? names.get(step.source_key) : undefined;
      const what = who ? `adds ${who} as a source` : 'may add a new source';
      return {
        text: step.interview_code
          ? `Review ${step.interview_code}: ${what}`
          : `Review the waiting evidence: ${what}`,
        done: false,
      };
    }
    case 'reading':
      return {
        text: `${step.interview_code ?? 'A'} transcript is being read${step.progress != null ? ` (${step.progress}%)` : ''}`,
        done: false,
      };
    case 'scheduled_interview':
      return {
        text: `${step.interview_code} is scheduled${step.scheduled_at ? ` for ${formatDay(step.scheduled_at)}` : ''}`,
        done: false,
      };
    case 'follow_up_waiting': {
      const who = step.person_id ? names.get(`person:${step.person_id}`) : undefined;
      return {
        text: `Follow-up sent${who ? ` to ${who}` : ''}, waiting for an answer`,
        done: false,
      };
    }
    default:
      return { text: 'Ask someone a follow-up, or add evidence from a document', done: false };
  }
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return ((parts[0]![0] ?? '') + (parts.length > 1 ? (parts.at(-1)![0] ?? '') : '')).toUpperCase();
}

// ---------------------------------------------------------------------------
// Setup and start (Decision 53.16, rule 6, rule 20)
// ---------------------------------------------------------------------------

export interface PlanDraft {
  name: string;
  email: string;
  /** `<input type="datetime-local">` value, empty when not scheduled. */
  when: string;
}

export const emptyDraft = (): PlanDraft => ({ name: '', email: '', when: '' });

export interface PlanGroup {
  name: string;
  rows: Array<{ index: number; role: PlanRole; code: string }>;
}

/**
 * The starting plan grouped by interview type, in plan order. Codes are
 * the ones the start assigns: S1…Sn in plan order (rule 20).
 */
export function planGroups(plan: ReadonlyArray<PlanRole>): PlanGroup[] {
  const groups: PlanGroup[] = [];
  plan.forEach((role, index) => {
    let g = groups.find((x) => x.name === role.interview_type_name);
    if (!g) groups.push((g = { name: role.interview_type_name, rows: [] }));
    g.rows.push({ index, role, code: `S${index + 1}` });
  });
  return groups;
}

export function toIsoDateTime(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function nameProblem(name: string): string | null {
  const t = name.trim();
  if (!t || t.split(/\s+/).length >= 2) return null;
  return 'First and last name, so the same person is recognised later.';
}

export function emailProblem(email: string): string | null {
  const t = email.trim();
  return !t || /^[^@\s]+@[^@\s]+$/.test(t) ? null : 'Not an email address.';
}

/** Rows given a date: they become scheduled interviews. */
export const scheduledRows = (drafts: ReadonlyArray<PlanDraft>) =>
  drafts.flatMap((d, i) => (d.when ? [i] : []));

/** A row's answer to the same-person question. */
export type DuplicateAnswer =
  | { decision: 'same'; plan_index: number }
  | { decision: 'same'; person_id: string }
  | { decision: 'different' };

export function toMatchDecision(
  answer: DuplicateAnswer,
  candidates: DuplicateRow['candidates'],
): MatchDecision {
  if (answer.decision === 'same') return answer;
  return {
    decision: 'different',
    candidate_ids: candidates.flatMap((c) => (c.person_id ? [c.person_id] : [])),
    plan_indexes: candidates.flatMap((c) => (c.plan_index != null ? [c.plan_index] : [])),
  };
}

/** The start request: one row per plan role, so a row's index is its plan position. */
export function buildStartBody(input: {
  frameworkId: string;
  plan: ReadonlyArray<PlanRole>;
  drafts: ReadonlyArray<PlanDraft>;
  questions: Readonly<Record<number, DuplicateRow['candidates']>>;
  answers: Readonly<Record<number, DuplicateAnswer>>;
  sendInvites: boolean | null;
}): StartDiscoveryBody {
  const plan = input.plan.map((role, i) => {
    const d = input.drafts[i] ?? emptyDraft();
    const name = d.name.trim().replace(/\s+/g, ' ');
    const answer = input.answers[i];
    const candidates = input.questions[i];
    const at = toIsoDateTime(d.when);
    return {
      interview_type_id: role.interview_type_id,
      ordinal: role.ordinal,
      ...(name
        ? {
            person: {
              full_name: name,
              ...(d.email.trim() ? { email: d.email.trim() } : {}),
              ...(answer && candidates
                ? { match_decision: toMatchDecision(answer, candidates) }
                : {}),
            },
          }
        : {}),
      ...(at ? { scheduled_at: at } : {}),
    };
  });
  return {
    framework_id: input.frameworkId,
    plan,
    ...(input.sendInvites !== null ? { send_invites: input.sendInvites } : {}),
  };
}

// ---------------------------------------------------------------------------
// Client facts (OQ-W): each value with its source and as-of date
// ---------------------------------------------------------------------------

export interface FactDraft {
  value: string;
  asOf: string;
  kind: FactSourceKind;
  note: string;
  documentId: string;
  page: string;
  evidenceNumber: string;
  /** The source editor is showing. */
  editingSource: boolean;
  /** The person chose a source themselves; a new value then keeps it. */
  sourceTouched: boolean;
  dirty: boolean;
}

export function todayIso(now = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

export function factDraftOf(fact: ClientFact): FactDraft {
  const s = fact.source;
  return {
    value: fact.value ?? '',
    asOf: fact.as_of ?? '',
    kind: s?.kind ?? 'manual',
    note: s?.note ?? '',
    documentId: s?.document_id ?? '',
    page: s?.page != null ? String(s.page) : '',
    evidenceNumber: s?.evidence_number ?? '',
    editingSource: false,
    sourceTouched: false,
    dirty: false,
  };
}

/**
 * A new value was typed. Unless the person already chose a source, the
 * old source no longer vouches for it: it becomes "Entered by hand",
 * as of today, until they say where it came from.
 */
export function withValue(d: FactDraft, value: string, today = todayIso()): FactDraft {
  const reset = d.sourceTouched
    ? {}
    : {
        kind: 'manual' as const,
        note: '',
        documentId: '',
        page: '',
        evidenceNumber: '',
        asOf: today,
      };
  return { ...d, ...reset, value, dirty: true, editingSource: true };
}

export function withSource(d: FactDraft, patch: Partial<FactDraft>): FactDraft {
  return { ...d, ...patch, dirty: true, editingSource: true, sourceTouched: true };
}

export function factProblem(d: FactDraft, today = todayIso()): string | null {
  if (!d.value.trim()) return 'Type the value.';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d.asOf)) return 'Give the date the value is true as of.';
  if (d.asOf > today) return 'The as-of date cannot be in the future.';
  if (d.kind === 'document' && !d.documentId) return 'Pick the document it came from.';
  if (d.kind === 'document' && d.page && !/^[1-9]\d*$/.test(d.page))
    return 'Page is a whole number.';
  if (d.kind === 'evidence' && !/^E-\d{3,}$/.test(d.evidenceNumber.trim())) {
    return 'Give the evidence number, for example E-012.';
  }
  return null;
}

export function factBody(d: FactDraft, fact: ClientFact): PutClientFactBody {
  const note = d.note.trim();
  const source: PutClientFactBody['source'] =
    d.kind === 'document'
      ? { kind: 'document', document_id: d.documentId, ...(d.page ? { page: Number(d.page) } : {}) }
      : d.kind === 'evidence'
        ? { kind: 'evidence', evidence_number: d.evidenceNumber.trim() }
        : { kind: 'manual' };
  if (note) source.note = note;
  return { value: d.value.trim(), as_of: d.asOf, source, base_fact_id: fact.id };
}
